import { useState, useRef, useEffect, useCallback } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { logError } from "@/lib/log";

const SOURCES_MARKER = "\n\n[[SOURCES]]";

export const GREETING = {
  id: "greeting",
  role: "assistant",
  content:
    "Hey, I'm Father's Compass. I know this road can feel heavy and lonely — you don't have to walk it alone. I'm here to offer supportive, educational guidance about custody and fathers' rights. I'm not a lawyer or therapist, so for your specific case I'll always point you to a licensed attorney. What's on your mind today?",
};

const FALLBACK = "I'm having trouble connecting right now. Please try again in a moment. If this is urgent, call or text 988.";

function splitSources(text) {
  const idx = text.indexOf(SOURCES_MARKER);
  if (idx === -1) return { content: text, sources: null };
  try { return { content: text.slice(0, idx), sources: JSON.parse(text.slice(idx + SOURCES_MARKER.length)) }; }
  catch { return { content: text.slice(0, idx), sources: null }; }
}

let counter = 0;
const nextId = () => `m-${Date.now()}-${counter++}`;

export function useCompassChat(open) {
  const [messages, setMessages] = useState([GREETING]);
  const [stateSel, setStateSel] = useState("Utah");
  const [streaming, setStreaming] = useState(false);
  const [mode, setMode] = useState("chat");
  const [researchEnabled, setResearchEnabled] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const sessionId = useRef(`sess-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/research/status`).then((r) => r.json()).then((d) => setResearchEnabled(!!d.enabled))
      .catch((e) => logError("Research status check failed", e));
  }, [open]);

  const replaceLast = useCallback((patch) => setMessages((m) => {
    const copy = [...m];
    copy[copy.length - 1] = { ...copy[copy.length - 1], ...patch };
    return copy;
  }), []);

  const pushPair = useCallback((userText, assistant, userAttachments = []) => setMessages((m) => [
    ...m, { id: nextId(), role: "user", content: userText, attachments: userAttachments }, { id: nextId(), role: "assistant", content: "", ...assistant }]), []);

  const addAttachment = useCallback(async (file) => {
    if (attachments.length >= 4) { toast.error("Up to 4 files per message."); return; }
    if (!/\.(jpe?g|png|webp|pdf)$/i.test(file.name)) { toast.error("Attach a JPG, PNG or WebP screenshot, or a PDF."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Files must be under 10 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/chat/attachments`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Upload failed");
      const preview = data.attachment.kind === "image" ? URL.createObjectURL(file) : null;
      setAttachments((a) => [...a, { ...data.attachment, preview }]);
      if (data.attachment.kind === "pdf" && !data.attachment.has_text) toast.warning("No readable text in that PDF—it may be scanned. A screenshot of the page works better.");
    } catch (e) { toast.error(e.message || "Upload failed"); } finally { setUploading(false); }
  }, [attachments.length]);

  const removeAttachment = useCallback((id) => setAttachments((a) => a.filter((x) => x.id !== id)), []);

  const runResearch = useCallback(async (msg) => {
    if (!stateSel) {
      pushPair(msg, { content: "Pick your state above first — live research is state-specific, and I want to pull the right statutes for you." });
      return;
    }
    pushPair(msg, { researching: true });
    setStreaming(true);
    try {
      const res = await fetch(`${API}/research`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ question: msg, state: stateSel, session_id: sessionId.current }) });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Research failed");
      replaceLast({ content: data.answer, sources: data.sources, research: true, researching: false, saved: data.saved });
    } catch (e) {
      logError("Research request failed", e);
      replaceLast({ content: e.message || "Research is unavailable right now. Try again shortly.", researching: false });
    } finally { setStreaming(false); }
  }, [stateSel, pushPair, replaceLast]);

  const runChat = useCallback(async (msg) => {
    const files = attachments;
    const shown = msg || (files.length ? "Please explain what this is and what I should know about it." : "");
    pushPair(shown, {}, files);
    setAttachments([]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ session_id: sessionId.current, message: msg, state: stateSel, attachment_ids: files.map((f) => f.id) }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(typeof d.detail === "string" ? d.detail : "Chat failed"); }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        replaceLast(splitSources(full));
      }
    } catch (e) {
      logError("Chat stream failed", e);
      replaceLast({ content: e.message && e.message !== "Chat failed" ? e.message : FALLBACK });
    } finally { setStreaming(false); }
  }, [stateSel, attachments, pushPair, replaceLast]);

  const send = useCallback((raw) => {
    const msg = (raw || "").trim();
    if (streaming || uploading) return;
    if (!msg && !(mode === "chat" && attachments.length)) return;
    return mode === "research" ? runResearch(msg) : runChat(msg);
  }, [mode, streaming, uploading, attachments.length, runResearch, runChat]);

  return { messages, stateSel, setStateSel, streaming, mode, setMode, researchEnabled, send, attachments, uploading, addAttachment, removeAttachment };
}
